import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition } from '@headlessui/react';
import { PiXLight, PiScrollLight, PiCheckCircleLight, PiWarningCircleLight } from 'react-icons/pi';
import Button from '../shared/TailwindComponents/Button';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import Spinner from '../shared/TailwindComponents/Spinner';
import { useAppDispatch } from '../../store';
import { importFilesSSE, fetchFolderTree } from '../../store/actions/FolderAction';
import { markAsContract } from '../../utils/contractsApi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Called after at least one contract was tracked, so the dashboard can refresh. */
  onUploaded?: () => void;
}

const CONTRACTS_FOLDER = 'Contracts';

type RowResult = { name: string; ok: boolean; error?: string };

/**
 * Upload one or more of the firm's own contracts into the Contracts library and
 * track each as a contract (extraction kicks off automatically).
 *
 * Reuses the platform's proven file-upload path (`importFilesSSE` → the same
 * encrypted blob storage every other upload uses); we only add the "mark as
 * contract" step after. Attaching an upload to a specific matter arrives with the
 * matter access-cascade work (Phase 3).
 */
const ContractUploadModal: React.FC<Props> = ({ isOpen, onClose, onUploaded }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<RowResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => { setFiles([]); setResults(null); setError(null); };
  const close = () => { if (!busy) { reset(); onClose(); } };

  /** Find an existing top-level "Contracts" folder id, or undefined to create one. */
  const resolveLibraryFolderId = async (): Promise<string | undefined> => {
    try {
      const tree: any[] = (await dispatch(fetchFolderTree() as any)) || [];
      const match = tree.find(
        (f) => !f?.parent_id && String(f?.label || '').trim().toLowerCase() === CONTRACTS_FOLDER.toLowerCase(),
      );
      return match?.id ? String(match.id) : undefined;
    } catch {
      return undefined;
    }
  };

  const submit = async () => {
    if (files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const folderId = await resolveLibraryFolderId();
      // Existing folder → FolderID; otherwise FolderName auto-creates the library.
      const payload: Record<string, any> = folderId
        ? { FolderID: folderId }
        : { FolderName: CONTRACTS_FOLDER };

      const res: any = await dispatch(importFilesSSE(payload, files) as any);
      const uploaded: any[] = res?.filesUploaded || [];
      if (!uploaded.length) {
        setError(t('Upload failed. Please try again.'));
        setBusy(false);
        return;
      }

      const rows: RowResult[] = [];
      for (const f of uploaded) {
        const fileUuid = f?.FileID || f?.file_id || f?.uuid;
        const name = f?.OriginalName || f?.name || t('Contract');
        if (!fileUuid) {
          rows.push({ name, ok: false, error: t('No file id returned') });
          continue;
        }
        try {
          await markAsContract('file', String(fileUuid));
          rows.push({ name, ok: true });
        } catch {
          rows.push({ name, ok: false, error: t('Uploaded, but could not be tracked') });
        }
      }
      setResults(rows);
      if (rows.some((r) => r.ok)) onUploaded?.();
    } catch (e: any) {
      setError(e?.message || t('Upload failed. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={close}>
        <Transition.Child as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-ink/20 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="glass w-full max-w-xl transform overflow-hidden rounded-lg shadow-sm transition-all">
                <div className="flex items-center justify-between border-b border-hairline-soft px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full border border-champagne/40 bg-champagne/10 text-champagne">
                      <PiScrollLight className="h-4.5 w-4.5" />
                    </span>
                    <div className="text-sm text-fg-strong">{t('Upload a contract')}</div>
                  </div>
                  <button onClick={close} className="text-fg-muted hover:text-fg-body" aria-label={t('Close')}>
                    <PiXLight className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-5 py-4">
                  {busy ? (
                    <div className="flex flex-col items-center justify-center py-10">
                      <Spinner size="sm" className="!h-10 !w-10 mb-3" />
                      <p className="text-sm text-fg-muted">{t('Uploading and tracking…')}</p>
                    </div>
                  ) : results ? (
                    <div className="space-y-2">
                      {results.map((r, i) => (
                        <div key={i} className="flex items-center gap-2.5 rounded-md border border-hairline-soft bg-surface-sunken/60 px-3 py-2">
                          {r.ok
                            ? <PiCheckCircleLight className="h-4 w-4 shrink-0 text-success-500" />
                            : <PiWarningCircleLight className="h-4 w-4 shrink-0 text-warning-500" />}
                          <span className="min-w-0 flex-1 truncate text-sm text-fg-body">{r.name}</span>
                          <span className="text-[11px] text-fg-muted">{r.ok ? t('Tracking') : r.error}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <p className="mb-3 text-xs text-fg-muted">
                        {t('Your contracts are added to the Contracts library and analysed automatically — parties, dates, renewals and risk.')}
                      </p>
                      <FileUploader files={files} onFilesChange={setFiles} />
                      {error && <p className="mt-2 text-sm text-danger-600 dark:text-danger-400">{error}</p>}
                    </>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-hairline-soft px-5 py-3">
                  {results ? (
                    <Button variant="primary" size="small" onClick={close}>{t('Done')}</Button>
                  ) : (
                    <>
                      <Button variant="neutral" size="small" onClick={close} disabled={busy}>{t('Cancel')}</Button>
                      <Button variant="primary" size="small" onClick={submit} disabled={busy || files.length === 0}>
                        {files.length > 1 ? t('Upload {{n}} contracts', { n: files.length }) : t('Upload contract')}
                      </Button>
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ContractUploadModal;
